import React, { useContext } from 'react';
import { StaticContext } from '@sm/webassets/StaticContext';
import { t, defineMessages } from '@sm/intl';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { StaticImageData } from 'next/image';
import HeroFormCTA from '~app/components/HeroFormCTA';
import heroImgV1 from '~static/images/dareToAsk/large-dark-text.webp';
import heroImgV2 from '~static/images/dareToAsk/large-light-text.webp';
import dareToAskUK from '~static/images/dareToAsk/dare-to-ask-uk.gif';
import bwaiGIFTest from '~static/images/bwaiTest/bwai_gif.gif';
import driveGetResults2025Variant1 from '~static/images/driveGetResults2025/driveGetResults2025Variant1.webp';
import driveGetResults2025Variant2 from '~static/images/driveGetResults2025/driveGetResults2025Variant2.webp';
import driveGetResults2025Variant3 from '~static/images/driveGetResults2025/driveGetResults2025Variant3.webp';
import useStyles from './useStyles';
import { UK_PATH_LINKS, US_PATH_LINKS } from '../constants';
import { SplitHeroProps } from '~helpers/types';
import SplitHeroImage from './SplitHeroImage';
import SplitHeroCTASection from './SplitHeroCTASection';

export const SPLIT_HERO_COPY = defineMessages({
  BUTTON_LABEL: {
    id: 'SplitHero.ButtonLabel',
    defaultMessage: 'Learn more',
    description: '[Type: label][Vis: high] - The text shown in the pages primary CTA',
  },
  IMAGE_LABEL: {
    id: 'SplitHero.ImageLabel',
    defaultMessage: 'SurveyMonkey Branding',
    description: '[Type: label][Vis: low] - The aria text shown in the pages primary image',
  },
});

const imageMap: { [key: string]: string | StaticImageData } = {
  dareToAskLightText: heroImgV2,
  dareToAskUK,
  bwaiGIFTest,
  rebrandEP2025: driveGetResults2025Variant2,
  driveGetResults2025Variant1,
  driveGetResults2025Variant2,
  driveGetResults2025Variant3,
  default: heroImgV1,
};

const mobileImageMap: { [key: string]: string | StaticImageData } = {
  rebrandEP2025: driveGetResults2025Variant2,
};

const getImageVariant = (splitHeroImage: string): string | StaticImageData =>
  imageMap[splitHeroImage] || imageMap.default;

const getUrls = (
  isUkSource: boolean,
  rebrandEP2025: boolean,
  isDriveGetResults2025Variant: boolean
): { primaryUrl: string; secondaryUrl: string } => {
  const primaryUrl =
    ((rebrandEP2025 || isDriveGetResults2025Variant) && '/welcome/get-real-results/') ||
    (isUkSource && UK_PATH_LINKS?.exploreUkCtaUrl) ||
    US_PATH_LINKS.exploreUsCtaUrl ||
    '/mp/dare-to-ask/';

  const secondaryUrl = (isUkSource && UK_PATH_LINKS?.ukSignupUrl) || '/sign-up';

  return { primaryUrl, secondaryUrl };
};

const getMobileImageVariant = (splitHeroImage: string): string | StaticImageData =>
  mobileImageMap[splitHeroImage] || '';

const SplitHero = ({
  boldHeader,
  formCopy,
  splitHeroDarkBackground = false,
  splitHeroCTA = false,
  splitHeroPrimaryCTA = '',
  splitHeroSecondaryCTA = '',
  splitHeroImage = '',
  subHeader: subHeaderContent,
  utSource,
  utSource2,
  buildWithAI = false,
  rebrandEP2025 = false,
  variant,
}: SplitHeroProps): React.ReactElement => {
  const classes = useStyles();
  const isUkSource = utSource2 === 'DareToAskPracticalUk';
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const imageVariant = getImageVariant(splitHeroImage);
  const mobileImageVariant = getMobileImageVariant(splitHeroImage);

  const isDriveGetResults2025Variant = [
    'driveGetResults2025Variant1',
    'driveGetResults2025Variant2',
    'driveGetResults2025Variant3',
    'rebrandEP2025',
  ].includes(variant ?? '');

  const metricsProps = {
    amplitudeEvent: 'endpage user interaction',
    templateType,
    surveyId,
    collectorId,
    respondentId,
    utSource,
    utSource2,
    utSource3: 'SplitHero',
  };
  const { primaryUrl, secondaryUrl } = getUrls(isUkSource, rebrandEP2025, isDriveGetResults2025Variant);
  const primaryCtaText = splitHeroPrimaryCTA || t(SPLIT_HERO_COPY.BUTTON_LABEL);

  const getBackgroundClass = (): string => {
    if (!splitHeroDarkBackground) {
      return '';
    }

    // Collect all possible variant-specific background classes
    const variantBackgroundClasses = [
      // Add more variant backgrounds here as needed
      variant && variant === 'driveGetResults2025Variant1' && classes.driveGetResults2025Variant1Bg,
      variant && variant === 'driveGetResults2025Variant2' && classes.driveGetResults2025Variant2Bg,
      variant && variant === 'rebrandEP2025' && classes.driveGetResults2025Variant2Bg,
      variant && variant === 'driveGetResults2025Variant3' && classes.driveGetResults2025Variant3Bg,
    ]
      .filter(Boolean)
      .join(' ');

    // Always include the base dark background if enabled
    return [classes.darkBackground, variantBackgroundClasses].filter(Boolean).join(' ');
  };

  return (
    <Box className={getBackgroundClass()} pt={splitHeroDarkBackground ? 10 : undefined}>
      <Grid noBleeds noGutters verticalGap={isDriveGetResults2025Variant ? 8 : 1}>
        <Grid.Item xl={6} lg={6} md={splitHeroCTA ? 12 : 6} sm={12} xs={12}>
          {splitHeroCTA ? (
            <SplitHeroCTASection
              classes={classes}
              variant={utSource2}
              boldHeader={boldHeader}
              subHeaderContent={subHeaderContent}
              splitHeroDarkBackground={splitHeroDarkBackground}
              buildWithAI={buildWithAI}
              primaryUrl={primaryUrl}
              primaryCtaText={primaryCtaText}
              signUpUrl={secondaryUrl}
              splitHeroSecondaryCTA={splitHeroSecondaryCTA}
              metricsProps={metricsProps}
            />
          ) : (
            <Box p={3}>
              <HeroFormCTA className={classes.ctaForm} utSource={utSource} utSource2={utSource2} {...formCopy} />
            </Box>
          )}
        </Grid.Item>
        <Grid.Item xl={6} lg={6} md={splitHeroCTA ? 12 : 6} sm={12} xs={12}>
          <SplitHeroImage
            classes={classes}
            imageVariant={imageVariant}
            mobileImageVariant={mobileImageVariant}
            imageAlt={t(SPLIT_HERO_COPY.IMAGE_LABEL)}
            {...metricsProps}
          />
        </Grid.Item>
      </Grid>
    </Box>
  );
};

export default SplitHero;
